import { useState, useEffect } from 'react';
import { PenSquare, X, Save, AlertTriangle, Lock } from 'lucide-react';
import { filesAPI } from '../api';
import './BulkEditor.css';

function BulkEditor({ onClose }) {
  const [allFiles, setAllFiles] = useState([]);
  const [editText, setEditText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [appendMode, setAppendMode] = useState(false);

  useEffect(() => { loadAllFiles(); }, []);

  const loadAllFiles = async () => {
    setIsLoading(true);
    try {
      const response = await filesAPI.getAll();
      setAllFiles(response.data.files);
    } catch (error) {
      setMessage('Ошибка загрузки файлов');
    } finally {
      setIsLoading(false);
    }
  };

  // Пишем только в активные файлы БЕЗ замка. Замок (`locked`) — отдельный от статуса флаг:
  // файл раздаётся клиентам как обычно, но общий текст в него не заливается.
  const activeFiles = allFiles.filter(f => f.mode !== 'nonactive' && !f.locked);
  const skippedFiles = allFiles.filter(f => f.mode === 'nonactive' && !f.locked);
  const lockedFiles = allFiles.filter(f => f.locked);

  const handleSave = async () => {
    if (!editText.trim()) { setMessage('Введите текст для сохранения'); return; }
    if (activeFiles.length === 0) { setMessage('Нет активных файлов для обновления'); return; }
    const skipParts = [];
    if (skippedFiles.length > 0) skipParts.push(`${skippedFiles.length} неактивных`);
    if (lockedFiles.length > 0) skipParts.push(`${lockedFiles.length} под замком`);
    const skipInfo = skipParts.length ? ` (пропущено: ${skipParts.join(', ')})` : '';
    const actionWord = appendMode ? 'дополнит' : 'перезапишет';
    if (!confirm(`Это ${actionWord} ${activeFiles.length} активных файлов${skipInfo}. Продолжить?`)) return;

    setIsSaving(true);
    setMessage('');
    setProgress({ current: 0, total: activeFiles.length });

    try {
      try {
        await filesAPI.getAll();
      } catch (error) {
        if (error.response?.status === 401) {
          setMessage('Сессия истекла. Страница будет перезагружена...');
          setTimeout(() => window.location.reload(), 1500);
          return;
        }
      }

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (let i = 0; i < activeFiles.length; i++) {
        const file = activeFiles[i];
        try {
          let newContent = editText;
          if (appendMode) {
            const { data } = await filesAPI.getOne(file.name);
            const oldContent = data.content || '';
            newContent = oldContent.trim() ? oldContent.replace(/\n+$/, '') + '\n' + editText : editText;
          }
          await filesAPI.update(file.name, newContent);
          successCount++;
          setProgress(p => ({ ...p, current: p.current + 1 }));
          if (i < activeFiles.length - 1) await new Promise(r => setTimeout(r, 300));
        } catch (error) {
          errorCount++;
          const errorMsg = error.response?.status === 401 ? 'Сессия истекла' : (error.response?.data?.error || error.message);
          errors.push({ name: file.name, error: errorMsg });
          if (error.response?.status === 401) {
            setMessage(`Сессия истекла после ${successCount} файлов.`);
            setTimeout(() => window.location.reload(), 2000);
            return;
          }
        }
      }

      const skippedNote =
        (skippedFiles.length > 0 ? `\n⏭ Пропущено (неактивных): ${skippedFiles.length}` : '') +
        (lockedFiles.length > 0 ? `\n🔒 Пропущено (под замком): ${lockedFiles.length}` : '');
      if (errorCount === 0) {
        setMessage(`✅ Обновлено файлов: ${successCount}${skippedNote}`);
        setTimeout(() => onClose(), 2000);
      } else {
        setMessage(`✅ Обновлено: ${successCount}\n❌ Ошибок: ${errorCount}${skippedNote}\n${errors.map(e => `• ${e.name}: ${e.error}`).join('\n')}`);
      }
    } catch (error) {
      setMessage('Ошибка сохранения: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bulk-editor-overlay">
      <div className="bulk-editor-modal">
        <div className="bulk-editor-header">
          <h2><PenSquare size={16} /> Массовое редактирование</h2>
          <button onClick={onClose} className="close-btn"><X size={15} /></button>
        </div>

        <div className="bulk-editor-info">
          <p>Текст ниже будет записан во <strong>активные файлы</strong> ({activeFiles.length} из {allFiles.length} шт).</p>
          {skippedFiles.length > 0 && (
            <p className="warning-text" style={{ color: 'var(--text-muted)' }}>
              <AlertTriangle size={13} /> Пропущено {skippedFiles.length} неактивных: {skippedFiles.map(f => f.name.replace(/\.json$/, '')).join(', ')}
            </p>
          )}
          {lockedFiles.length > 0 && (
            <p className="warning-text" style={{ color: 'var(--text-muted)' }}>
              <Lock size={13} /> Пропущено {lockedFiles.length} под замком: {lockedFiles.map(f => f.name.replace(/\.json$/, '')).join(', ')}
            </p>
          )}
          <p className="warning-text">
            <AlertTriangle size={13} /> Все активные файлы получат одинаковое содержимое!
          </p>
          <label className="bulk-checkbox-row">
            <input
              type="checkbox"
              checked={appendMode}
              onChange={(e) => setAppendMode(e.target.checked)}
            />
            Добавить к старым ссылкам (не заменять)
          </label>
          {isSaving && progress.total > 0 && (
            <div className="progress-section">
              <div className="progress-label">Сохранено {progress.current} из {progress.total}...</div>
              <div className="progress-bar-track">
                <div className="progress-bar-fill" style={{ width: `${(progress.current / progress.total) * 100}%` }} />
              </div>
            </div>
          )}
          {message && (
            <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="bulk-editor-loading">
            <div className="spinner"></div>
            <p>Загрузка файлов...</p>
          </div>
        ) : (
          <div className="bulk-editor-content">
            <textarea
              className="bulk-textarea"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              placeholder="Вставьте VPN-ссылки или любой текст..."
              spellCheck={false}
            />
          </div>
        )}

        <div className="bulk-editor-footer">
          <span className="file-count">Активных: {activeFiles.length} / {allFiles.length}</span>
          <div className="bulk-actions">
            <button onClick={onClose} className="btn btn-secondary" disabled={isSaving}>Отмена</button>
            <button onClick={handleSave} className="btn btn-success" disabled={isSaving || isLoading}>
              <Save size={14} /> {isSaving ? 'Сохранение...' : (appendMode ? 'Добавить ко всем' : 'Сохранить все')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BulkEditor;
